import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, LogOut, RefreshCw, MessageSquare, Clock3, CheckCircle2, XCircle, Plus, Pencil, MapPin, FileSpreadsheet, ExternalLink, Bell, CalendarOff, Trash2, Undo2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import Brand from "../components/Brand";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ReservationEditor from "../components/ReservationEditor";
import ClosedDatesDialog from "../components/ClosedDatesDialog";

const STATUS_META = {
  pending:   { label: "Pendente",   className: "bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30", icon: Clock3 },
  confirmed: { label: "Confirmada", className: "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30", icon: CheckCircle2 },
  rejected:  { label: "Rejeitada",  className: "bg-white/5 text-[#A19D98] border-white/15", icon: XCircle },
};

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  const [reservations, setReservations] = useState([]);
  const [trashed, setTrashed] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, rejected: 0, trashed: 0 });
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [closedOpen, setClosedOpen] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);

  // Refs for live-polling
  const knownPendingIdsRef = useRef(null); // null = uninitialized; Set once populated
  const pollingRef = useRef(null);
  const audioCtxRef = useRef(null);

  // Short "bell" beep via WebAudio (no external asset)
  const playChime = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      [880, 1320].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.value = 0.0001;
        o.connect(g); g.connect(ctx.destination);
        const t = ctx.currentTime + i * 0.14;
        g.gain.exponentialRampToValueAtTime(0.2, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
        o.start(t); o.stop(t + 0.3);
      });
    } catch (err) { console.debug("[chime] blocked:", err?.message); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [resRes, trashRes, logsRes, statsRes, sheetsRes] = await Promise.all([
        api.get("/reservations"),
        api.get("/reservations", { params: { deleted: true } }).catch(() => ({ data: [] })),
        api.get("/sms-logs"),
        api.get("/stats"),
        api.get("/sheets/status").catch(() => ({ data: null })),
      ]);
      setReservations(resRes.data);
      setTrashed(trashRes.data);
      setSmsLogs(logsRes.data);
      setStats(statsRes.data);
      setSheetsStatus(sheetsRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Live polling: every 15s check for new pending reservations (no full reload)
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/reservations", { params: { status: "pending" } });
        const ids = new Set(data.map((r) => r.id));
        const known = knownPendingIdsRef.current;
        if (known === null) {
          // First successful fetch — snapshot and skip notification
          knownPendingIdsRef.current = ids;
        } else {
          const newOnes = data.filter((r) => !known.has(r.id));
          if (newOnes.length > 0) {
            newOnes.forEach((r) => {
              toast(`Nova reserva: ${r.name}`, {
                description: `${r.date} · ${r.time} · ${r.party_size} ${r.party_size === 1 ? "pessoa" : "pessoas"}${r.notes ? ` — ${r.notes}` : ""}`,
                duration: 8000,
                action: { label: "Ver", onClick: () => setTab("pending") },
              });
            });
            playChime();
            setUnseenCount((c) => c + newOnes.length);
            // Refresh the full lists so the new pendings appear in the table
            loadAll();
          }
          knownPendingIdsRef.current = ids;
        }
      } catch (err) { console.debug("[poll] failed:", err?.message); }
    };
    poll();
    pollingRef.current = setInterval(poll, 15000);
    return () => clearInterval(pollingRef.current);
  }, [loadAll, playChime]);

  // Tab-title badge with unseen count
  useEffect(() => {
    const base = "LAMUSIK — Dashboard";
    document.title = unseenCount > 0 ? `(${unseenCount}) ${base}` : base;
  }, [unseenCount]);

  // When user switches to "pending" tab, clear the unseen counter
  useEffect(() => {
    if (tab === "pending") setUnseenCount(0);
  }, [tab]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Split active reservations into "upcoming" (today or future) and "past".
  // Compare on ISO YYYY-MM-DD which sorts lexicographically = chronologically.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const filtered = useMemo(
    () => reservations.filter((r) => r.status === tab && r.date >= todayIso),
    [reservations, tab, todayIso]
  );

  // Confirmed reservations grouped by date, ascending. Only upcoming (today+future).
  const confirmedByDay = useMemo(() => {
    const confirmed = reservations.filter((r) => r.status === "confirmed" && r.date >= todayIso);
    const map = new Map();
    for (const r of confirmed) {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date).push(r);
    }
    const dates = Array.from(map.keys()).sort(); // ISO yyyy-mm-dd sorts lexicographically = chronologically
    return dates.map((date) => ({
      date,
      rows: map.get(date).sort((a, b) => a.time.localeCompare(b.time)),
    }));
  }, [reservations, todayIso]);

  // Past reservations — any status — sorted by date desc (most recent first), then time desc.
  const pastList = useMemo(() => {
    return reservations
      .filter((r) => r.date < todayIso)
      .sort((a, b) => {
        if (a.date !== b.date) return b.date.localeCompare(a.date);
        return b.time.localeCompare(a.time);
      });
  }, [reservations, todayIso]);

  const doAction = async (id, kind) => {
    try {
      await api.patch(`/reservations/${id}/${kind}`);
      toast.success(kind === "confirm" ? "Reserva confirmada." : "Reserva rejeitada.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro na operação.");
    }
  };

  const toggleArrived = async (id) => {
    try {
      await api.patch(`/reservations/${id}/arrived`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao marcar chegada.");
    }
  };

  const softDelete = async (id) => {
    if (!window.confirm("Enviar esta reserva para o lixo? O lugar fica livre.")) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Reserva movida para o lixo.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao apagar.");
    }
  };

  const restoreDeleted = async (id) => {
    try {
      await api.post(`/reservations/${id}/restore`);
      toast.success("Reserva recuperada.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao recuperar.");
    }
  };

  const permanentDelete = async (id) => {
    if (!window.confirm("Apagar DEFINITIVAMENTE? Esta acção não pode ser revertida.")) return;
    try {
      await api.delete(`/reservations/${id}/permanent`);
      toast.success("Reserva apagada definitivamente.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao apagar.");
    }
  };

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (r) => { setEditing(r); setEditorOpen(true); };

  return (
    <div className="min-h-screen bg-black text-[#F4EFEB]">
      <header className="border-b border-white/5 px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-lg z-20">
        <div className="flex items-center gap-6">
          <Brand size="sm" />
          <span className="hidden md:inline text-xs uppercase tracking-[0.3em] text-[#A19D98]">/ Gestão de Reservas</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-[#34C759] border border-[#34C759]/30" data-testid="live-indicator" title="Atualização automática a cada 15s">
            <span className="h-1.5 w-1.5 rounded-full bg-[#34C759] animate-pulse" />
            ao vivo
          </div>
          <Button variant="ghost" onClick={() => setClosedOpen(true)} data-testid="btn-closed-dates"
            className="text-[#A19D98] hover:text-[#D45A2C] hover:bg-transparent rounded-none border border-white/10 px-4">
            <CalendarOff className="h-4 w-4 mr-2" /> Encerrar dia
          </Button>
          <Button variant="ghost" onClick={openNew} data-testid="btn-new-reservation"
            className="text-[#D45A2C] hover:text-[#F4EFEB] hover:bg-transparent rounded-none border border-[#D45A2C]/40 px-4">
            <Plus className="h-4 w-4 mr-2" /> Nova reserva
          </Button>
          <Button variant="ghost" onClick={loadAll} data-testid="btn-refresh"
            className="text-[#A19D98] hover:text-[#D45A2C] hover:bg-transparent rounded-none">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button variant="ghost" onClick={handleLogout} data-testid="btn-logout"
            className="text-[#A19D98] hover:text-[#D45A2C] hover:bg-transparent rounded-none">
            <LogOut className="h-4 w-4 mr-2" /> Sair
          </Button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="accent-rule" />
          <h1 className="font-display text-4xl md:text-5xl mt-5">Dashboard</h1>
          <p className="text-sm text-[#A19D98] mt-2">
            Gere reservas, marque chegadas e consulte o histórico. Ações sincronizam com o Google Sheets.
          </p>
        </motion.div>

        {/* Sheets status badge */}
        <SheetsBadge status={sheetsStatus} />

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stats-grid">
          <StatCard label="Pendentes"   value={stats.pending}   accent="#FF9500" />
          <StatCard label="Confirmadas" value={stats.confirmed} accent="#34C759" />
          <StatCard label="Rejeitadas"  value={stats.rejected}  accent="#A19D98" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-12">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-8 p-0 h-auto overflow-x-auto">
            {[
              { v: "pending",   l: "Pendentes" },
              { v: "confirmed", l: "Confirmadas" },
              { v: "rejected",  l: "Rejeitadas" },
              { v: "sms",       l: "SMS" },
              { v: "trash",     l: "Eliminadas" },
              { v: "past",      l: "Passadas" },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="bg-transparent rounded-none px-0 py-4 text-xs uppercase tracking-[0.25em] text-[#A19D98] data-[state=active]:text-[#D45A2C] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[#D45A2C] flex items-center gap-2">
                {t.l}
                {t.v === "pending" && unseenCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-[#D45A2C] text-white rounded-full animate-pulse" data-testid="unseen-badge">
                    {unseenCount}
                  </span>
                )}
                {t.v === "trash" && stats.trashed > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-white/10 text-[#A19D98] rounded-full" data-testid="trash-badge">
                    {stats.trashed}
                  </span>
                )}
                {t.v === "past" && pastList.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-white/10 text-[#A19D98] rounded-full" data-testid="past-badge">
                    {pastList.length}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {["pending", "rejected"].map((s) => (
            <TabsContent key={s} value={s} className="mt-8">
              {loading ? (
                <EmptyState text="A carregar..." />
              ) : filtered.length === 0 ? (
                <EmptyState text={`Sem reservas ${STATUS_META[s].label.toLowerCase()}.`} />
              ) : (
                <ReservationList
                  rows={filtered}
                  tab={s}
                  onConfirm={(id) => doAction(id, "confirm")}
                  onReject={(id) => doAction(id, "reject")}
                  onEdit={openEdit}
                  onArrived={toggleArrived}
                  onDelete={softDelete}
                />
              )}
            </TabsContent>
          ))}

          {/* Confirmadas — agrupadas por dia, ordem cronológica */}
          <TabsContent value="confirmed" className="mt-8">
            {loading ? (
              <EmptyState text="A carregar..." />
            ) : confirmedByDay.length === 0 ? (
              <EmptyState text="Sem reservas confirmadas." />
            ) : (
              <div className="space-y-8" data-testid="confirmed-by-day">
                {confirmedByDay.map(({ date, rows }) => (
                  <DayGroup key={date} date={date} count={rows.length}>
                    <ReservationList
                      rows={rows}
                      tab="confirmed"
                      onConfirm={(id) => doAction(id, "confirm")}
                      onReject={(id) => doAction(id, "reject")}
                      onEdit={openEdit}
                      onArrived={toggleArrived}
                      onDelete={softDelete}
                    />
                  </DayGroup>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Lixo — reservas soft-deleted */}
          <TabsContent value="trash" className="mt-8">
            {loading ? (
              <EmptyState text="A carregar..." />
            ) : trashed.length === 0 ? (
              <EmptyState text="Nada eliminado." />
            ) : (
              <TrashList
                rows={trashed}
                onRestore={restoreDeleted}
                onPermanent={permanentDelete}
              />
            )}
          </TabsContent>

          {/* Passadas — reservas activas com data anterior a hoje */}
          <TabsContent value="past" className="mt-8">
            {loading ? (
              <EmptyState text="A carregar..." />
            ) : pastList.length === 0 ? (
              <EmptyState text="Sem reservas passadas." />
            ) : (
              <PastList
                rows={pastList}
                onDelete={softDelete}
              />
            )}
          </TabsContent>

          <TabsContent value="sms" className="mt-8">
            <SmsLogList logs={smsLogs} loading={loading} />
          </TabsContent>
        </Tabs>
      </main>

      <ReservationEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        reservation={editing}
        onSaved={() => loadAll()}
      />
      <ClosedDatesDialog
        open={closedOpen}
        onOpenChange={setClosedOpen}
      />
    </div>
  );
}

function SheetsBadge({ status }) {
  if (!status) return null;
  const { configured, spreadsheet_url, share_email } = status;
  return (
    <div className="mt-8 border border-white/5 bg-[#0A0A0A] px-5 py-3 flex items-center gap-3 text-xs" data-testid="sheets-badge">
      <FileSpreadsheet className={`h-4 w-4 ${configured ? "text-[#34C759]" : "text-[#FF9500]"}`} />
      <span className="uppercase tracking-[0.22em] text-[#A19D98]">Google Sheets</span>
      <span className="text-[#6b6763]">·</span>
      {configured ? (
        <>
          <span className="text-[#34C759]">Sincronizado</span>
          <span className="text-[#6b6763]">partilhado com {share_email}</span>
          {spreadsheet_url && (
            <a href={spreadsheet_url} target="_blank" rel="noreferrer" className="ml-auto text-[#D45A2C] hover:text-[#F4EFEB] flex items-center gap-1.5">
              Abrir folha <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </>
      ) : (
        <span className="text-[#FF9500]">Não configurado — credenciais em falta no servidor</span>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-white/5 bg-[#0E0E0E] p-6 flex items-end justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#A19D98]">{label}</div>
        <div className="font-display text-5xl mt-3" style={{ color: accent }}>{value}</div>
      </div>
      <div className="h-12 w-[2px]" style={{ background: accent, opacity: 0.5 }} />
    </div>
  );
}

function ReservationList({ rows, tab, onConfirm, onReject, onEdit, onArrived, onDelete }) {
  return (
    <div className="divide-y divide-white/5 border border-white/5 bg-[#0A0A0A]">
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#6b6763]">
        <div className="col-span-2">Cliente</div>
        <div className="col-span-2">Telemóvel</div>
        <div className="col-span-2">Data / Hora</div>
        <div className="col-span-1">Pax</div>
        <div className="col-span-2">Notas internas</div>
        <div className="col-span-3 text-right">Ações</div>
      </div>
      {rows.map((r) => {
        const Meta = STATUS_META[r.status];
        const arrived = r.arrived;
        return (
          <div key={r.id} data-testid={`reservation-row-${r.id}`}
            className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 items-start hover:bg-white/[0.02] transition-colors ${arrived ? "bg-[#34C759]/5" : ""}`}>
            <div className="col-span-2">
              <div className="text-[#F4EFEB] flex items-center gap-2">
                {r.name}
                {arrived && (
                  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#34C759] border border-[#34C759]/30 px-1.5 py-0.5">
                    <MapPin className="h-2.5 w-2.5" /> Chegou
                  </span>
                )}
              </div>
              <div className="text-xs text-[#6b6763] mt-1">{r.notes ? `“${r.notes}”` : `#${r.id.slice(0, 8)}`}</div>
            </div>
            <div className="col-span-2 text-[#A19D98] text-sm">{r.phone}</div>
            <div className="col-span-2 text-sm">
              <div className="text-[#F4EFEB]">{r.date}</div>
              <div className="text-[#A19D98]">{r.time}</div>
            </div>
            <div className="col-span-1 text-[#F4EFEB]">{r.party_size}</div>
            <div className="col-span-2 text-xs text-[#A19D98] line-clamp-3" data-testid={`admin-notes-${r.id}`}>
              {r.admin_notes || <span className="text-[#6b6763]">—</span>}
            </div>
            <div className="col-span-3 flex justify-start md:justify-end items-center gap-2 flex-wrap">
              {tab === "pending" && (
                <>
                  <button onClick={() => onConfirm(r.id)} data-testid={`btn-confirm-${r.id}`}
                    className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/10 transition-colors">
                    <Check className="h-3.5 w-3.5" /> Confirmar
                  </button>
                  <button onClick={() => onReject(r.id)} data-testid={`btn-reject-${r.id}`}
                    className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-white/15 text-[#A19D98] hover:border-[#FF3B30]/40 hover:text-[#FF3B30] transition-colors">
                    <X className="h-3.5 w-3.5" /> Rejeitar
                  </button>
                </>
              )}

              {tab === "confirmed" && (
                <button onClick={() => onArrived(r.id)} data-testid={`btn-arrived-${r.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border transition-colors ${
                    arrived
                      ? "border-[#34C759] bg-[#34C759] text-black hover:bg-[#2AB653]"
                      : "border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/10"
                  }`}>
                  <MapPin className="h-3.5 w-3.5" /> {arrived ? "Chegou ✓" : "Marcar chegada"}
                </button>
              )}

              {tab !== "rejected" && (
                <button onClick={() => onEdit(r)} data-testid={`btn-edit-${r.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:border-white/30 hover:text-[#F4EFEB] transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </button>
              )}

              {onDelete && (
                <button onClick={() => onDelete(r.id)} data-testid={`btn-delete-${r.id}`}
                  className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:border-[#FF3B30]/40 hover:text-[#FF3B30] transition-colors"
                  title="Enviar para o lixo">
                  <Trash2 className="h-3.5 w-3.5" /> Apagar
                </button>
              )}

              {tab === "rejected" && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border ${Meta.className}`}>
                  <Meta.icon className="h-3 w-3" /> {Meta.label}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayGroup({ date, count, children }) {
  // Format "2026-04-24" -> "quinta, 24 de abril" (PT)
  const d = new Date(date + "T00:00:00");
  const weekday = new Intl.DateTimeFormat("pt-PT", { weekday: "long" }).format(d);
  const dayMonth = new Intl.DateTimeFormat("pt-PT", { day: "numeric", month: "long" }).format(d);
  const today = new Date().toISOString().slice(0, 10);
  const isToday = date === today;
  const isTomorrow = date === new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const label = isToday ? "Hoje" : isTomorrow ? "Amanhã" : weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return (
    <section data-testid={`day-group-${date}`}>
      <header className="flex items-end justify-between mb-3 pb-2 border-b border-white/5">
        <div className="flex items-baseline gap-3">
          <h3 className={`font-display text-2xl ${isToday ? "text-[#D45A2C]" : "text-[#F4EFEB]"}`}>
            {label}
          </h3>
          <span className="text-xs uppercase tracking-[0.25em] text-[#6b6763]">{dayMonth}</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-[#A19D98]">
          {count} {count === 1 ? "reserva" : "reservas"}
        </span>
      </header>
      {children}
    </section>
  );
}

function TrashList({ rows, onRestore, onPermanent }) {
  return (
    <div className="divide-y divide-white/5 border border-white/5 bg-[#0A0A0A]" data-testid="trash-list">
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#6b6763]">
        <div className="col-span-2">Cliente</div>
        <div className="col-span-2">Telemóvel</div>
        <div className="col-span-2">Data / Hora</div>
        <div className="col-span-1">Pax</div>
        <div className="col-span-2">Estado anterior</div>
        <div className="col-span-3 text-right">Ações</div>
      </div>
      {rows.map((r) => {
        const Meta = STATUS_META[r.status] || STATUS_META.rejected;
        return (
          <div key={r.id} data-testid={`trash-row-${r.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 items-start hover:bg-white/[0.02] transition-colors opacity-70">
            <div className="col-span-2">
              <div className="text-[#F4EFEB]">{r.name}</div>
              <div className="text-xs text-[#6b6763] mt-1">{r.notes ? `"${r.notes}"` : `#${r.id.slice(0, 8)}`}</div>
            </div>
            <div className="col-span-2 text-[#A19D98] text-sm">{r.phone}</div>
            <div className="col-span-2 text-sm">
              <div className="text-[#F4EFEB]">{r.date}</div>
              <div className="text-[#A19D98]">{r.time}</div>
            </div>
            <div className="col-span-1 text-[#F4EFEB]">{r.party_size}</div>
            <div className="col-span-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border ${Meta.className}`}>
                <Meta.icon className="h-3 w-3" /> {Meta.label}
              </span>
              {r.deleted_at && (
                <div className="text-[10px] text-[#6b6763] mt-1">
                  Apagada {new Date(r.deleted_at).toLocaleString("pt-PT")}
                </div>
              )}
            </div>
            <div className="col-span-3 flex justify-start md:justify-end items-center gap-2 flex-wrap">
              <button onClick={() => onRestore(r.id)} data-testid={`btn-restore-${r.id}`}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-[#34C759]/30 text-[#34C759] hover:bg-[#34C759]/10 transition-colors">
                <Undo2 className="h-3.5 w-3.5" /> Recuperar
              </button>
              <button onClick={() => onPermanent(r.id)} data-testid={`btn-permanent-${r.id}`}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Apagar definitivamente
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PastList({ rows, onDelete }) {
  // Group by date desc for readability
  const groups = [];
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.date)) {
      map.set(r.date, []);
      groups.push(r.date);
    }
    map.get(r.date).push(r);
  }
  return (
    <div className="space-y-8" data-testid="past-list">
      {groups.map((date) => (
        <section key={date}>
          <header className="flex items-end justify-between mb-3 pb-2 border-b border-white/5">
            <h3 className="font-display text-2xl text-[#A19D98]">
              {new Intl.DateTimeFormat("pt-PT", { day: "numeric", month: "long", year: "numeric" }).format(new Date(date + "T00:00:00"))}
            </h3>
            <span className="text-[10px] uppercase tracking-widest text-[#6b6763]">
              {map.get(date).length} {map.get(date).length === 1 ? "reserva" : "reservas"}
            </span>
          </header>
          <div className="divide-y divide-white/5 border border-white/5 bg-[#0A0A0A]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] uppercase tracking-[0.22em] text-[#6b6763]">
              <div className="col-span-3">Cliente</div>
              <div className="col-span-2">Telemóvel</div>
              <div className="col-span-1">Hora</div>
              <div className="col-span-1">Pax</div>
              <div className="col-span-2">Estado</div>
              <div className="col-span-3 text-right">Ações</div>
            </div>
            {map.get(date).map((r) => {
              const Meta = STATUS_META[r.status] || STATUS_META.rejected;
              return (
                <div key={r.id} data-testid={`past-row-${r.id}`}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-start hover:bg-white/[0.02] transition-colors ${r.arrived ? "bg-[#34C759]/5" : ""}`}>
                  <div className="col-span-3">
                    <div className="text-[#F4EFEB] flex items-center gap-2">
                      {r.name}
                      {r.arrived && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#34C759] border border-[#34C759]/30 px-1.5 py-0.5">
                          <MapPin className="h-2.5 w-2.5" /> Chegou
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6b6763] mt-1">{r.notes ? `"${r.notes}"` : `#${r.id.slice(0, 8)}`}</div>
                  </div>
                  <div className="col-span-2 text-[#A19D98] text-sm">{r.phone}</div>
                  <div className="col-span-1 text-[#F4EFEB]">{r.time}</div>
                  <div className="col-span-1 text-[#F4EFEB]">{r.party_size}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border ${Meta.className}`}>
                      <Meta.icon className="h-3 w-3" /> {Meta.label}
                    </span>
                  </div>
                  <div className="col-span-3 flex justify-start md:justify-end items-center gap-2 flex-wrap">
                    <button onClick={() => onDelete(r.id)} data-testid={`btn-past-delete-${r.id}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-white/10 text-[#A19D98] hover:border-[#FF3B30]/40 hover:text-[#FF3B30] transition-colors">
                      <Trash2 className="h-3.5 w-3.5" /> Apagar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function SmsLogList({ logs, loading }) {
  if (loading) return <EmptyState text="A carregar..." />;
  if (logs.length === 0) return <EmptyState text="Sem SMS simulados ainda." />;
  return (
    <div className="space-y-3" data-testid="sms-log-list">
      {logs.map((l) => {
        const delivered = l.delivered !== false;
        return (
          <div key={l.id} className={`border bg-[#0A0A0A] p-5 flex gap-4 ${delivered ? "border-white/5" : "border-[#FF3B30]/40"}`}>
            <div className={`shrink-0 h-9 w-9 rounded-none border flex items-center justify-center ${
              !delivered ? "border-[#FF3B30]/40 text-[#FF3B30]" :
              l.kind === "confirmation" ? "border-[#34C759]/30 text-[#34C759]" : "border-white/15 text-[#A19D98]"
            }`}>
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 text-xs">
                <span className="text-[#F4EFEB]">→ {l.phone}</span>
                <span className="text-[#6b6763] uppercase tracking-widest text-[10px]">{l.kind === "confirmation" ? "confirmação" : "rejeição"}</span>
                <span className={`uppercase tracking-widest text-[10px] ${delivered ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
                  {delivered ? "· enviado" : "· falhou"}
                </span>
                <span className="ml-auto text-[#6b6763] text-[10px]">{new Date(l.sent_at).toLocaleString("pt-PT")}</span>
              </div>
              <p className="mt-2 text-sm text-[#A19D98] leading-relaxed">{l.message}</p>
              {!delivered && l.provider_detail && (
                <p className="mt-2 text-[11px] text-[#FF3B30] font-mono">{l.provider_detail}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-white/10 py-20 text-center text-sm uppercase tracking-[0.25em] text-[#6b6763]">
      {text}
    </div>
  );
}
